import { useMemo } from 'react';
import { TestLike } from '../panels/synthUdeMetrics/buildMetricQueries';
import { FilterOption } from './types';

interface UseSynthFilterOptionsArgs {
  test: TestLike;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  $syn?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  $labels?: any;
}

interface SynthFilterOptions {
  agentOptions: FilterOption[];
  targetOptions: FilterOption[];
  siteOptions: FilterOption[];
  asnOptions: FilterOption[];
  labelOptions: FilterOption[];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toAgentOptions($syn: any, ids: Array<number | string>): FilterOption[] {
  return ids
    .map((id: number | string) => {
      const agent = $syn?.agents.get(String(id));
      if (!agent) {
        return null;
      }
      return {
        value: String(id),
        label: agent.get('agent_alias') || agent.get('agent_name') || String(id),
        agent
      };
    })
    .filter(Boolean) as FilterOption[];
}

/**
 * Derives the option lists for every synth filter from the test config + $syn/$labels stores.
 * Kept separate from SynthFilterBar so layout and derivation are decoupled and reusable.
 */
export default function useSynthFilterOptions({ test, $syn, $labels }: UseSynthFilterOptionsArgs): SynthFilterOptions {
  // $syn.agents can hydrate after first render; cache-bust the agent-derived memos on its fetch state
  const agentsFetched = $syn?.agents?.hasFetched;
  const agentsLastUpdated = $syn?.agents?.lastUpdated;

  const agentOptions = useMemo<FilterOption[]>(
    () => toAgentOptions($syn, (test.get('config.agents') || []) as Array<number | string>),
    [test, $syn, agentsFetched, agentsLastUpdated]
  );

  // Labels scoped to only those associated with agents in this test.
  // $labels hydrates independently of the route's storeLoader, so cache-bust on the
  // collection's fetch state the way useLabelOptions does — otherwise late labels never appear.
  const labelsFetched = $labels?.labels?.hasFetched;
  const labelsLastUpdated = $labels?.labels?.lastUpdated;
  const labelOptions = useMemo<FilterOption[]>(() => {
    if (!$labels) {
      return [];
    }
    const agentIds = (test.get('config.agents') || []) as Array<number | string>;
    const labelMap = new Map<string, FilterOption>();

    for (const agentId of agentIds) {
      const agentLabels = $labels.getLabels('synth_agent', agentId);
      for (const lbl of agentLabels) {
        const id = String(lbl.get('id'));
        if (!labelMap.has(id)) {
          labelMap.set(id, {
            value: id,
            label: String(lbl.get('name')),
            bg: String(lbl.get('color') || '#888'),
            color: '#ffffff'
          });
        }
      }
    }
    return Array.from(labelMap.values());
  }, [test, $labels, labelsFetched, labelsLastUpdated]);

  // Sites scoped to only those associated with agents in this test
  const siteOptions = useMemo<FilterOption[]>(() => {
    const agentIds = (test.get('config.agents') || []) as Array<number | string>;
    const siteMap = new Map<string, FilterOption>();

    for (const agentId of agentIds) {
      const agent = $syn?.agents.get(String(agentId));
      if (agent) {
        const siteTitle = agent.get('site.title') as string | undefined;
        const siteId = agent.get('site.id') || agent.get('site_id');
        if (siteId && String(siteId) !== '0' && siteTitle) {
          const id = String(siteId);
          if (!siteMap.has(id)) {
            siteMap.set(id, { value: id, label: siteTitle });
          }
        }
      }
    }
    return Array.from(siteMap.values());
  }, [test, $syn, agentsFetched, agentsLastUpdated]);

  // ASNs scoped to only those associated with agents in this test
  const asnOptions = useMemo<FilterOption[]>(() => {
    const agentIds = (test.get('config.agents') || []) as Array<number | string>;
    const asnMap = new Map<string, FilterOption>();

    for (const agentId of agentIds) {
      const agent = $syn?.agents.get(String(agentId));
      if (agent) {
        const asn = agent.get('asn');
        const asName = agent.get('as_name');
        if (asn && String(asn) !== '0') {
          const id = String(asn);
          if (!asnMap.has(id)) {
            asnMap.set(id, { value: id, label: asName ? `${asName} (${asn})` : `AS${asn}` });
          }
        }
      }
    }
    return Array.from(asnMap.values());
  }, [test, $syn, agentsFetched, agentsLastUpdated]);

  const targetOptions = useMemo<FilterOption[]>(() => {
    if (test.isDns) {
      const servers = (test.get('config.servers') || '') as string;
      return servers
        .split(',')
        .map((s: string) => s.trim())
        .filter(Boolean)
        .map((s: string) => ({ value: s, label: s }));
    }
    if (test.isMesh) {
      // dst_agent — same pool as src agents
      return agentOptions;
    }
    if (test.isAgentTest) {
      // Sources only become destinations when the target agent tests back to them
      const targetAgent = test.get('config.target.value');
      const sources = test.get('config.reciprocal')
        ? ((test.get('config.agents') || []) as Array<number | string>)
        : [];
      const ids = [...new Set([targetAgent, ...sources].filter((id) => id != null && id !== '').map(String))];
      return toAgentOptions($syn, ids);
    }
    const target = test.get('config.target.value') || '';
    const targets =
      typeof target === 'string'
        ? target
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean)
        : [];
    return targets.map((t) => ({ value: t, label: t }));
  }, [test, $syn, agentOptions, agentsFetched, agentsLastUpdated]);

  return { agentOptions, targetOptions, siteOptions, asnOptions, labelOptions };
}
